import React from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { PostOrPage } from '@tryghost/content-api';
import { ArticleJsonLd, NextSeo } from 'next-seo';
import clsx from 'clsx';

import Ghost from '@/services/ghost';
import InfoJson from '@/config/info.json';

import classes from '@/styles/blog.module.css';
import { FORMAT_DATE2, formatDate } from '@/utility/date';

export interface BlogDetailPageProps {
  post: PostOrPage;
}

export default function BlogDetailPage({ post }: BlogDetailPageProps) {
  return (
    <>
      <ArticleJsonLd
        title={post.title}
        description={post.excerpt}
        images={[post.feature_image]}
        authorName={post.primary_author?.name}
        dateModified={post.updated_at}
        datePublished={post.published_at}
        url={`/blogs/${post.slug}`}
      />
      <NextSeo
        title={post.title}
        description={post.og_description}
        openGraph={{
          images: [
            {
              url: post.feature_image,
              alt: post.title,
              height: 100,
              width: 100,
            },
          ],
          description: post.og_description,
          title: post.title,
          url: `/blogs/${post.slug}`,
          site_name: 'Degaan Real Estate',
          locale: 'en',
          profile: {
            firstName: post.primary_author?.name,
          },
        }}
      />
      <div className="bg-gray-100 py-4 md:py-8">
        <div className="max-w-6xl bg-white mx-auto  rounded-md">
          <div
            style={{
              backgroundImage: `url(${post.feature_image})`,
            }}
            className={classes['post-header-cover']}
          >
            <div className={clsx(classes.inner, 'px-8')}>
              <h6 className="text-white font-bold relative">
                <span>{InfoJson.name} | </span>
                <span>{formatDate(post.created_at, FORMAT_DATE2)} | </span>
                <span>{post.reading_time} min read</span>
              </h6>
              <h1
                className={clsx(classes['post-title'], 'text-2xl md:text-6xl')}
              >
                {post.title}
              </h1>
            </div>
          </div>
          <div
            className={clsx(
              'prose lg:prose-lg prose-blue prose-sm max-w-4xl mx-auto px-4 md:px-8 py-4 md:py-8',
              classes.content
            )}
          >
            <div dangerouslySetInnerHTML={{ __html: post.html }}></div>
          </div>
        </div>
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async (context) => {
  const locales = context.locales;

  const languageWisePaths = await Promise.all(
    locales.map(async (locale) => {
      const posts = await Ghost.posts.browse({
        limit: 'all',
        include: ['authors'],
        filter: ['tag:blog', `tag:blog-${locale}`],
      });

      return posts.map((post) => ({
        params: { slug: post.slug },
        locale: locale,
      }));
    })
  );

  const paths = languageWisePaths.flat();

  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<BlogDetailPageProps> = async (
  context
) => {
  const post = await Ghost.posts.read({ slug: context.params.slug as string });

  if (!post) {
    return {
      notFound: true,
    };
  }

  return {
    props: { post },
    revalidate: 120,
  };
};
